import { createStorefrontApiClient } from '@shopify/storefront-api-client'
import { isBrowser } from "@helpers"
import {
  CART_CREATE_MUTATION,
  CART_QUERY,
  CART_LINES_ADD_MUTATION,
  CART_LINES_REMOVE_MUTATION,
  CART_LINES_UPDATE_MUTATION
} from '../graphql/cart'
import { variantForOptions, fetchProduct } from './productHelpers'

// Check if environment variables are properly loaded
const shopifyDomain = process.env.GATSBY_SHOPIFY_DOMAIN || "minus18"
const accessToken = process.env.GATSBY_STOREFRONT_ACCESS_TOKEN

// Enhanced environment validation
if (!accessToken) {
  if (typeof window !== 'undefined') {
    console.error('Missing GATSBY_STOREFRONT_ACCESS_TOKEN environment variable')
  } else {
    // During build time, log but don't throw
    console.warn('GATSBY_STOREFRONT_ACCESS_TOKEN not available during build - this is expected')
  }
}

// Only create client if we have access token
let storefrontClient = null
if (accessToken) {
  storefrontClient = createStorefrontApiClient({
    storeDomain: `${shopifyDomain}.myshopify.com`,
    publicAccessToken: accessToken,
    apiVersion: '2024-10',
    customFetchApi: (url, init) => {
      if (typeof window !== undefined) {
        return fetch(url, init)
      }
    }
  })
}

// Create a client object that mimics the old SDK structure
const client = {
  // Storefront API client methods - with null check
  request: storefrontClient ? storefrontClient.request.bind(storefrontClient) : () => {
    throw new Error('Shopify client not initialized - missing access token')
  },

  // Product helpers that mimic the old SDK
  product: {
    fetch: fetchProduct,
    helpers: {
      variantForOptions
    }
  }
}

const createCart = async () => {
  // Guard against SSR and missing client
  if (!storefrontClient) {
    throw new Error('Shopify client not initialized')
  }

  if (typeof window === 'undefined') {
    throw new Error('Cart creation not available during server-side rendering')
  }

  try {
    console.log('Creating cart with Shopify Storefront API...')
    const { data, errors } = await storefrontClient.request(CART_CREATE_MUTATION, {
      variables: {
        input: {}
      }
    })

    console.log('Cart creation response:', { data, errors })

    if (errors?.length) {
      console.error('Cart creation errors:', errors)
      throw new Error(errors[0].message)
    }

    if (!data || !data.cartCreate || !data.cartCreate.cart) {
      console.error('Invalid cart creation response:', data)
      throw new Error('Invalid response from Shopify API')
    }

    if (data.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
      console.error('Cart creation user errors:', data.cartCreate.userErrors)
      throw new Error(data.cartCreate.userErrors[0].message)
    }

    return data.cartCreate.cart
  } catch (error) {
    console.error('Failed to create cart:', error)
    throw error
  }
}

const getCart = async (cartId) => {
  if (!storefrontClient) {
    throw new Error('Shopify client not initialized')
  }

  try {
    const { data, errors } = await storefrontClient.request(CART_QUERY, {
      variables: { id: cartId }
    })

    if (errors?.length) {
      console.error('Cart fetch errors:', errors)
      throw new Error(errors[0].message)
    }

    if (!data) {
      throw new Error('No data received from Shopify API')
    }

    return data.cart
  } catch (error) {
    console.error('Failed to fetch cart:', error)
    throw error
  }
}

const addLinesToCart = async (cartId, lines) => {
  if (!storefrontClient) {
    throw new Error('Shopify client not initialized')
  }

  try {
    const { data, errors } = await storefrontClient.request(CART_LINES_ADD_MUTATION, {
      variables: { cartId, lines }
    })

    if (errors?.length) {
      console.error('Add lines errors:', errors)
      throw new Error(errors[0].message)
    }

    return data.cartLinesAdd.cart
  } catch (error) {
    console.error('Failed to add lines to cart:', error)
    throw error
  }
}

const removeLinesFromCart = async (cartId, lineIds) => {
  if (!storefrontClient) {
    throw new Error('Shopify client not initialized')
  }

  try {
    const { data, errors } = await storefrontClient.request(CART_LINES_REMOVE_MUTATION, {
      variables: { cartId, lineIds }
    })

    if (errors?.length) {
      console.error('Remove lines errors:', errors)
      throw new Error(errors[0].message)
    }

    return data.cartLinesRemove.cart
  } catch (error) {
    console.error('Failed to remove lines from cart:', error)
    throw error
  }
}

const updateLinesInCart = async (cartId, lines) => {
  if (!storefrontClient) {
    throw new Error('Shopify client not initialized')
  }

  try {
    const { data, errors } = await storefrontClient.request(CART_LINES_UPDATE_MUTATION, {
      variables: { cartId, lines }
    })

    if (errors?.length) {
      console.error('Update lines errors:', errors)
      throw new Error(errors[0].message)
    }

    return data.cartLinesUpdate.cart
  } catch (error) {
    console.error('Failed to update lines in cart:', error)
    throw error
  }
}

// Lazy cart creation that only runs client-side
const getNewCheckoutPromise = async () => {
  if (typeof window === 'undefined') {
    throw new Error('Checkout creation not available during server-side rendering')
  }
  return createCart()
}

let cartPromise = null

const forceResetCart = () => {
  if (isBrowser) {
    // Clear the persisted cart state
    const persistKey = "persist:root" // or whatever your key is
    const persistedData = JSON.parse(localStorage.getItem(persistKey) || "{}")

    if (persistedData.cart) {
      const cartState = JSON.parse(persistedData.cart)
      // Remove checkoutId
      delete cartState.checkoutId

      // Update storage
      persistedData.cart = JSON.stringify(cartState)
      localStorage.setItem(persistKey, JSON.stringify(persistedData))

      // Reload to apply changes
      // window.location.reload();
    }
  }
}

// Initialize cart promise with better error handling
const initializeCart = () => {
  if (isBrowser && accessToken && storefrontClient) {
    return createCart().catch(error => {
      console.error('Failed to initialize cart:', error)
      return null // Return null instead of throwing
    })
  }
  return Promise.resolve(null)
}

// Only initialize if we're in browser and have token
if (isBrowser && accessToken) {
  cartPromise = initializeCart()
} else if (isBrowser && !accessToken) {
  console.warn('Cannot create cart: Missing Shopify access token')
  cartPromise = Promise.resolve(null)
} else {
  // During SSR, set to null promise
  cartPromise = Promise.resolve(null)
}

export {
  client,
  cartPromise,
  getNewCheckoutPromise,
  createCart,
  getCart,
  addLinesToCart,
  removeLinesFromCart,
  updateLinesInCart,
  forceResetCart
}